import { apiClient } from './api'

// Cache the timezone setting
let cachedTimezone: string | null = null
let cacheExpiry: number = 0

/**
 * Get the configured timezone from admin settings
 * Defaults to America/New_York if not configured or on error
 */
export async function getConfiguredTimezone(): Promise<string> {
  // Check cache first
  if (cachedTimezone && Date.now() < cacheExpiry) {
    return cachedTimezone
  }

  try {
    // Use apiClient which enforces HTTPS for .housepoints.ai domains
    const response = await apiClient.get('/api/settings/public/system')
    const timezone = response.data?.timezone || 'America/New_York'
    
    // Cache for 5 minutes
    cachedTimezone = timezone
    cacheExpiry = Date.now() + (5 * 60 * 1000)
    
    return timezone
  } catch (error) {
    console.warn('Failed to fetch timezone from settings, using default', error)
    // Cache the default to avoid repeated failures
    cachedTimezone = 'America/New_York'
    cacheExpiry = Date.now() + (60 * 1000) // Only cache for 1 minute on failure
    return 'America/New_York'
  }
}

/**
 * Clear the timezone cache (useful when settings are updated)
 */
export function clearTimezoneCache(): void {
  cachedTimezone = null
  cacheExpiry = 0
}

/**
 * Get current time in the configured timezone
 */
export async function getCurrentTimeInTimezone(): Promise<Date> {
  const timezone = await getConfiguredTimezone()
  const now = new Date()
  const timeString = now.toLocaleString("en-US", { timeZone: timezone })
  return new Date(timeString)
}

/**
 * Convert a date to the configured timezone
 */
export async function toConfiguredTimezone(date: Date | string): Promise<Date> {
  const timezone = await getConfiguredTimezone()
  const inputDate = typeof date === 'string' ? new Date(date) : date
  const timeString = inputDate.toLocaleString("en-US", { timeZone: timezone })
  return new Date(timeString)
}

/**
 * Check if it's past a certain hour in the configured timezone
 */
export async function isPastHourInTimezone(hour: number): Promise<boolean> {
  const timezone = await getConfiguredTimezone()
  const now = new Date()
  const timeString = now.toLocaleString("en-US", { timeZone: timezone })
  const localTime = new Date(timeString)
  return localTime.getHours() >= hour
}

/**
 * Format a date in the configured timezone
 */
export async function formatInTimezone(
  date: Date | string,
  formatString: string = 'MM/dd/yyyy h:mm a'
): Promise<string> {
  const timezone = await getConfiguredTimezone()
  const inputDate = typeof date === 'string' ? new Date(date) : date

  // Use Intl.DateTimeFormat for proper timezone formatting
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }

  return new Intl.DateTimeFormat('en-US', options).format(inputDate)
}

/**
 * Get the current date as yyyy-MM-dd string in the configured timezone
 * This is more reliable than Date object comparisons for date-only logic
 */
export async function getTodayDateString(): Promise<string> {
  const timezone = await getConfiguredTimezone()
  const now = new Date()

  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }

  const formatter = new Intl.DateTimeFormat('en-US', options)
  const parts = formatter.formatToParts(now)

  const year = parts.find(p => p.type === 'year')?.value
  const month = parts.find(p => p.type === 'month')?.value
  const day = parts.find(p => p.type === 'day')?.value

  return `${year}-${month}-${day}`
}

/**
 * Get tomorrow's date as yyyy-MM-dd string in the configured timezone
 */
export async function getTomorrowDateString(): Promise<string> {
  const timezone = await getConfiguredTimezone()
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000)

  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }

  const formatter = new Intl.DateTimeFormat('en-US', options)
  const parts = formatter.formatToParts(tomorrow)

  const year = parts.find(p => p.type === 'year')?.value
  const month = parts.find(p => p.type === 'month')?.value
  const day = parts.find(p => p.type === 'day')?.value

  return `${year}-${month}-${day}`
}

/**
 * Format a time string (HH:MM or HH:MM:SS) to 12-hour format with AM/PM
 * Uses family timezone settings when available
 */
export function formatTimeString(timeString: string | undefined): string {
  if (!timeString) return ''

  // Parse HH:MM or HH:MM:SS format
  const parts = timeString.split(':')
  if (parts.length < 2) return timeString

  const hours = parseInt(parts[0])
  const minutes = parts[1]

  // Convert to 12-hour format
  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours % 12 || 12

  return `${displayHours}:${minutes} ${period}`
}

/**
 * Format a time range (start - end) handling null values gracefully
 * Returns empty string if both are null, single time if only one exists
 */
export function formatTimeRange(startTime: string | undefined | null, endTime: string | undefined | null): string {
  const start = startTime ? formatTimeString(startTime) : ''
  const end = endTime ? formatTimeString(endTime) : ''

  if (start && end) {
    return `${start} - ${end}`
  } else if (start) {
    return `Starts ${start}`
  } else if (end) {
    return `Ends ${end}`
  }

  return ''
}

/**
 * Extract date portion from a UTC datetime string in the family timezone
 * Use this instead of .split('T')[0] to avoid timezone bugs
 *
 * CRITICAL: The Go backend serializes timestamps in UTC, so a due date of
 * "Dec 10, 8pm EST" becomes "2025-12-11T01:00:00Z" - extracting the date
 * naively with .split('T')[0] gives "2025-12-11" (WRONG!)
 *
 * This function correctly converts to family timezone first:
 * "2025-12-11T01:00:00Z" in EST family → "2025-12-10" (CORRECT)
 *
 * @param dateTimeStr - ISO datetime string from backend
 * @returns Date string in YYYY-MM-DD format in family timezone
 */
export async function extractDateInTimezone(dateTimeStr: string): Promise<string> {
  const result = await extractDateTimeInTimezone(dateTimeStr)
  return result.date
}

/**
 * Synchronous version using cached timezone (call after timezone is initialized)
 * Use this in filter/map callbacks where async isn't practical
 *
 * IMPORTANT: Ensure getConfiguredTimezone() has been called at least once
 * before using this function (e.g., in component's useEffect on mount)
 *
 * @param dateTimeStr - ISO datetime string from backend OR date-only string
 * @returns Date string in YYYY-MM-DD format in family timezone
 */
export function extractDateInTimezoneSync(dateTimeStr: string): string {
  if (!dateTimeStr) return ''

  // CRITICAL: Date-only strings (YYYY-MM-DD) should NOT be converted!
  // JavaScript's new Date("2026-01-05") interprets as midnight UTC,
  // which when converted to EST becomes 7pm on Jan 4 - shifting the date back!
  // Only ISO datetime strings (with 'T') need timezone conversion.

  // Check if this is a date-only string (no time component)
  // Date-only formats: "2026-01-05", "2026/01/05", "01-05-2026", etc.
  if (!dateTimeStr.includes('T') && !dateTimeStr.includes(' ')) {
    // Already a date-only string - return first 10 chars (YYYY-MM-DD)
    // Handle potential formats like "2026-01-05" by returning as-is if it looks like a date
    if (/^\d{4}-\d{2}-\d{2}/.test(dateTimeStr)) {
      return dateTimeStr.substring(0, 10)
    }
    return dateTimeStr
  }

  // If no timezone cached yet, use America/New_York as default (same as getConfiguredTimezone)
  // This ensures consistency for demo family and newly created families from template
  const timezone = cachedTimezone || 'America/New_York'

  // Parse the datetime string
  const date = new Date(dateTimeStr)
  if (isNaN(date.getTime())) {
    // Invalid date - try extracting from string directly as fallback
    if (dateTimeStr.includes('T')) return dateTimeStr.split('T')[0]
    if (dateTimeStr.includes(' ')) return dateTimeStr.split(' ')[0]
    return dateTimeStr
  }

  // Use Intl.DateTimeFormat to convert to family timezone
  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }
  const formatter = new Intl.DateTimeFormat('en-US', options)
  const parts = formatter.formatToParts(date)

  const year = parts.find(p => p.type === 'year')?.value
  const month = parts.find(p => p.type === 'month')?.value
  const day = parts.find(p => p.type === 'day')?.value

  return `${year}-${month}-${day}`
}

/**
 * Parse a due_date string (either date-only "2026-03-17" or full ISO datetime
 * "2026-03-18T00:00:00Z") into a Date object representing the correct local date.
 * Handles timezone conversion so UTC timestamps map to the right family-local date.
 */
export function parseDueDate(dueDateStr: string | null | undefined): Date {
  if (!dueDateStr) return new Date(NaN)
  const dateOnly = extractDateInTimezoneSync(dueDateStr)
  return new Date(dateOnly + 'T12:00:00')
}

/**
 * Convert UTC datetime string to date and time components in family timezone
 * Used for editing assignments - ensures date/time inputs show family local time, not UTC
 *
 * @param utcDateTimeString - ISO string like '2025-11-10T01:00:00Z'
 * @returns Object with date (YYYY-MM-DD) and time (HH:MM) in family timezone
 */
export async function extractDateTimeInTimezone(utcDateTimeString: string): Promise<{ date: string; time: string }> {
  const timezone = await getConfiguredTimezone()
  const utcDate = new Date(utcDateTimeString)

  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }

  const formatter = new Intl.DateTimeFormat('en-US', options)
  const parts = formatter.formatToParts(utcDate)

  const year = parts.find(p => p.type === 'year')?.value || ''
  const month = parts.find(p => p.type === 'month')?.value || ''
  const day = parts.find(p => p.type === 'day')?.value || ''
  const hour = parts.find(p => p.type === 'hour')?.value || '00'
  const minute = parts.find(p => p.type === 'minute')?.value || '00'

  return {
    date: `${year}-${month}-${day}`,
    time: `${hour}:${minute}`
  }
}